// ---------------------------------------------------------------------------
// Campaign fallback form (OPUS-D14)
// ---------------------------------------------------------------------------
// Rendered inside `/campaigns/fallback`. Lets the user:
//   - Enter a campaign ID (text input — later agents may upgrade to a picker)
//   - Choose which targeting reason the fallback covers (geo / device / os /
//     any). "Any" is the catch-all and is the dash-default.
//   - Paste an https:// URL the tracker should redirect to.
//   - Toggle the fallback on/off. Off deletes the row; on upserts.
//
// Design rails (CLAUDE.md):
//   light theme; emerald → sky gradient heading; Inter; 6–8px rounded
//   corners; zinc borders; no emojis; no competitor names.
//
// Validation is handled by the hand-rolled `validateFallbackInput` helper in
// `./api.ts` — react-hook-form and zod are deliberately not in the dash
// lockfile (package.json is root-owned).
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState, type FormEvent } from "react";
import { toast } from "sonner";

import {
	FALLBACK_REASONS,
	REASON_LABEL,
	extractErrorMessage,
	useFallbackList,
	useSaveFallback,
	validateFallbackInput,
	type CampaignFallbackView,
	type FallbackFormErrors,
	type FallbackFormInput,
	type FallbackReason,
} from "./api";

// ---------------------------------------------------------------------------
// Top-level form
// ---------------------------------------------------------------------------

interface FormState {
	campaignId: string;
	fallbackUrl: string;
	reason: FallbackReason;
	enabled: boolean;
}

const EMPTY_FORM: FormState = {
	campaignId: "",
	fallbackUrl: "",
	reason: "any",
	enabled: true,
};

export function FallbackForm() {
	const [form, setForm] = useState<FormState>(EMPTY_FORM);
	const [errors, setErrors] = useState<FallbackFormErrors>({});
	const [touched, setTouched] = useState<
		Partial<Record<keyof FormState, boolean>>
	>({});

	const trimmedCampaignId = form.campaignId.trim();
	const activeLookupId =
		trimmedCampaignId.length > 0 ? trimmedCampaignId : undefined;

	const listQuery = useFallbackList(activeLookupId);
	const saveMutation = useSaveFallback();

	// When the campaign + reason combo lands on an existing row, prime the
	// url + enabled toggle so the user sees the current state.
	const existingForReason = useMemo<CampaignFallbackView | null>(() => {
		const rows = listQuery.data ?? [];
		return rows.find((r) => r.reason === form.reason) ?? null;
	}, [listQuery.data, form.reason]);

	useEffect(() => {
		if (!activeLookupId) return;
		if (!listQuery.isSuccess) return;
		// Only overwrite if the user has not already edited the URL field.
		if (touched.fallbackUrl) return;
		setForm((prev) => ({
			...prev,
			fallbackUrl: existingForReason?.fallback_url ?? "",
			enabled: existingForReason ? true : prev.enabled,
		}));
	}, [
		activeLookupId,
		existingForReason,
		listQuery.isSuccess,
		touched.fallbackUrl,
	]);

	function update<K extends keyof FormState>(key: K, value: FormState[K]) {
		setForm((prev) => ({ ...prev, [key]: value }));
		setTouched((prev) => ({ ...prev, [key]: true }));
	}

	function runValidation(next: FormState): FallbackFormErrors {
		const input: FallbackFormInput = {
			campaignId: next.campaignId,
			fallbackUrl: next.fallbackUrl,
			reason: next.reason,
			enabled: next.enabled,
		};
		return validateFallbackInput(input);
	}

	function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const nextErrors = runValidation(form);
		setErrors(nextErrors);
		setTouched({
			campaignId: true,
			fallbackUrl: true,
			reason: true,
			enabled: true,
		});
		if (Object.keys(nextErrors).length > 0) {
			toast.error("Fix the highlighted fields before saving.");
			return;
		}
		saveMutation.mutate(
			{
				campaignId: form.campaignId.trim(),
				fallbackUrl: form.fallbackUrl.trim(),
				reason: form.reason,
				enabled: form.enabled,
			},
			{
				onSuccess: (_row, vars) => {
					toast.success(
						vars.enabled
							? "Fallback saved."
							: "Fallback disabled for this reason.",
					);
				},
				onError: (err) => {
					toast.error(extractErrorMessage(err));
				},
			},
		);
	}

	const isBusy = saveMutation.isPending;
	const submitLabel = form.enabled
		? existingForReason
			? "Save changes"
			: "Save fallback"
		: "Disable fallback";

	return (
		<form
			noValidate
			onSubmit={handleSubmit}
			aria-label="Campaign fallback"
			className="flex flex-col gap-6 rounded-[8px] border border-zinc-200 bg-white p-6 shadow-[0_1px_3px_0_rgb(24_24_27_/_0.08),0_4px_8px_-2px_rgb(24_24_27_/_0.06)]"
		>
			<Field
				htmlFor="fallback-campaign-id"
				label="Campaign ID"
				hint="The campaign whose blocked clicks should redirect to the fallback."
				error={touched.campaignId ? errors.campaignId : undefined}
			>
				<input
					id="fallback-campaign-id"
					type="text"
					autoComplete="off"
					spellCheck={false}
					value={form.campaignId}
					onChange={(e) => update("campaignId", e.target.value)}
					onBlur={() =>
						setErrors((prev) => ({ ...prev, ...runValidation(form) }))
					}
					placeholder="cmp_01h9y..."
					maxLength={128}
					className="w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 font-mono text-sm text-zinc-900 focus:border-[color:var(--color-brand-emerald)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-brand-emerald)]/20"
				/>
			</Field>

			<Field
				htmlFor="fallback-reason"
				label="Failure reason"
				hint="Pick which blocked-click trigger should use this redirect."
				error={touched.reason ? errors.reason : undefined}
			>
				<select
					id="fallback-reason"
					value={form.reason}
					onChange={(e) =>
						update("reason", e.target.value as FallbackReason)
					}
					className="w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-[color:var(--color-brand-emerald)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-brand-emerald)]/20"
				>
					{FALLBACK_REASONS.map((reason) => (
						<option key={reason} value={reason}>
							{REASON_LABEL[reason]}
						</option>
					))}
				</select>
			</Field>

			<div className="flex items-start gap-3 rounded-[6px] border border-zinc-200 bg-zinc-50/70 px-4 py-3">
				<input
					id="fallback-enabled"
					type="checkbox"
					checked={form.enabled}
					onChange={(e) => update("enabled", e.target.checked)}
					className="mt-0.5 h-4 w-4 rounded border-zinc-400 accent-[color:var(--color-brand-emerald)]"
				/>
				<label
					htmlFor="fallback-enabled"
					className="flex flex-col gap-1 text-sm text-zinc-700"
				>
					<span className="font-semibold text-zinc-900">
						Fallback enabled
					</span>
					<span className="text-xs text-zinc-500">
						When off, blocked clicks for this reason are dropped instead of
						redirected. Saving with this off deletes the row for the selected
						reason.
					</span>
				</label>
			</div>

			<Field
				htmlFor="fallback-url"
				label="Fallback URL"
				hint="Must be an absolute https:// URL. Up to 2048 characters."
				error={touched.fallbackUrl ? errors.fallbackUrl : undefined}
			>
				<input
					id="fallback-url"
					type="url"
					inputMode="url"
					autoComplete="off"
					spellCheck={false}
					disabled={!form.enabled}
					value={form.fallbackUrl}
					onChange={(e) => update("fallbackUrl", e.target.value)}
					onBlur={() =>
						setErrors((prev) => ({ ...prev, ...runValidation(form) }))
					}
					placeholder="https://offers.example.com/backup"
					maxLength={2048}
					className="w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-[color:var(--color-brand-emerald)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-brand-emerald)]/20 disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-500"
				/>
			</Field>

			<footer className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<ExistingSummary
					campaignId={trimmedCampaignId}
					isLoading={listQuery.isLoading}
					isError={listQuery.isError}
					rows={listQuery.data ?? []}
				/>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={() => {
							setForm(EMPTY_FORM);
							setErrors({});
							setTouched({});
						}}
						disabled={isBusy}
						className="inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-zinc-300 disabled:cursor-not-allowed disabled:opacity-60"
					>
						Reset
					</button>
					<button
						type="submit"
						disabled={isBusy}
						className="inline-flex items-center justify-center rounded-[6px] bg-gradient-to-r from-[color:var(--color-brand-emerald)] to-[color:var(--color-brand-sky)] px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-brand-sky)]/40 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{isBusy ? "Saving..." : submitLabel}
					</button>
				</div>
			</footer>
		</form>
	);
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function Field({
	htmlFor,
	label,
	hint,
	error,
	children,
}: {
	htmlFor: string;
	label: string;
	hint?: string;
	error?: string;
	children: React.ReactNode;
}) {
	return (
		<div className="flex flex-col gap-1">
			<label
				htmlFor={htmlFor}
				className="text-xs font-semibold uppercase tracking-wide text-zinc-600"
			>
				{label}
			</label>
			{children}
			{error ? (
				<p className="text-[11px] font-semibold text-rose-600" role="alert">
					{error}
				</p>
			) : hint ? (
				<p className="text-[11px] text-zinc-500">{hint}</p>
			) : null}
		</div>
	);
}

function ExistingSummary({
	campaignId,
	isLoading,
	isError,
	rows,
}: {
	campaignId: string;
	isLoading: boolean;
	isError: boolean;
	rows: CampaignFallbackView[];
}) {
	if (!campaignId) {
		return (
			<p className="text-xs text-zinc-500">
				Enter a campaign ID to view existing fallbacks.
			</p>
		);
	}
	if (isLoading) {
		return <p className="text-xs text-zinc-500">Loading existing rows...</p>;
	}
	if (isError) {
		return (
			<p className="text-xs text-rose-600">
				Could not load fallbacks for this campaign.
			</p>
		);
	}
	if (rows.length === 0) {
		return (
			<p className="text-xs text-zinc-500">
				No fallbacks configured yet for this campaign.
			</p>
		);
	}
	return (
		<ul className="flex flex-wrap items-center gap-2 text-[11px] text-zinc-600">
			{rows.map((row) => (
				<li
					key={row.id}
					className="inline-flex items-center gap-1 rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5"
				>
					<span className="font-semibold text-zinc-800">
						{REASON_LABEL[row.reason]}
					</span>
					<span aria-hidden="true" className="text-zinc-300">
						·
					</span>
					<span className="max-w-[180px] truncate text-zinc-500">
						{row.fallback_url}
					</span>
				</li>
			))}
		</ul>
	);
}

export default FallbackForm;
